import {
  Button,
  StapleTableBody,
  StapleTable,
  StapleTableHeader,
  StapleTableHeading,
  StapleTableRow,
  StapleTableCell,
  Tooltip,
  colors,
} from '@insurancemenu/imenu-components-lib';
import { Delete, Download, InfoButtonFilled } from '@insurancemenu/imenu-components-lib/dist/icons';
import { AlignmentTypes, ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { UPLOAD_FILE_TYPES } from '@insurancemenu/imenu-components-lib/dist/components/FileUpload';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useTriggerRenewalsPageStore } from 'store/pages/tools/trigger-renewals/pageStore';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { ModalType } from '@insurancemenu/imenu-components-lib/dist/helpers/ModalsHelper';
import CollapsiblePreviewSection from 'components/common/trigger-renewals/CollapsiblePreviewSection';
import {
  ActionButtonsStyled,
  FileNameStyled,
  ActionModalStyled,
  SelectStyled,
  StapleTableCellStyled,
  StapleTableHeadingStyled,
  ActionPanelStyled,
  UploadedFilesSectionTitleStyled,
  FileUploadStyled,
  ButtonStyled,
  NoDataMessageStyled,
} from './styles';

const ManageExhibitsModal: React.FC = observer(() => {
  const {
    renewalGroupActionDelegate: { manageExhibitsModalDelegate },
  } = useTriggerRenewalsPageStore();

  const {
    isVisible,
    isLoading,
    exhibitFiles,
    productLineOptions,
    selectedProductLineId,
    canSaveFile,
    subtitle,
    fileUploadDelegate,
    close,
    setSelectedProductLineId,
    removeEnrollmentExhibits,
    downloadExhibits,
    saveFile,
  } = manageExhibitsModalDelegate;

  const areExhibitsAdded = exhibitFiles.length > 0;
  const showExhibitsTable = areExhibitsAdded || isLoading;
  const [showUploadNewFileSection, setShowUploadNewFileSection] = useState(false);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    setShowUploadNewFileSection(!areExhibitsAdded);
  }, [isVisible, areExhibitsAdded]);

  function onDownloadExhibit(fileUrl: string): void {
    downloadByUrl(fileUrl);
    toastManager.addToast('Downloading exhibits...', ToastColorTypes.SUCCESS);
  }

  function onCollapseToggle(): void {
    setShowUploadNewFileSection((prev) => !prev);
  }

  function onClose(): void {
    setShowUploadNewFileSection(false);
    close();
  }

  function onUploadNewFile(): void {
    void saveFile();
    setShowUploadNewFileSection(false);
  }

  return (
    <ActionModalStyled
      title="Manage Exhibits"
      subtitle={subtitle}
      modalType={ModalType.NO_ICON}
      show={isVisible}
      onClose={onClose}
      modalContent={
        <>
          {!showExhibitsTable && (
            <NoDataMessageStyled>
              <InfoButtonFilled width="2.4rem" height="2.4rem" color={colors.yellow[600]} />
              No exhibits uploaded yet — upload the first one using the form below
            </NoDataMessageStyled>
          )}
          <CollapsiblePreviewSection
            title="Upload New File"
            subtitle="Associate each upload with a product line"
            show={showUploadNewFileSection}
            onToggle={onCollapseToggle}
            toggleButtonText="Upload Form"
          >
            <ActionPanelStyled>
              <SelectStyled
                label="Product Line"
                name="productLineId"
                options={productLineOptions}
                value={selectedProductLineId}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
                  setSelectedProductLineId(e.target.value);
                }}
                validationPassed={!!selectedProductLineId}
                validationMessage="Select the product line"
              />
              <ButtonStyled
                color="custom"
                customColor={colors.green[600]}
                onClick={onUploadNewFile}
                disabled={!canSaveFile}
                size="large"
                width="20rem"
              >
                Upload Files
              </ButtonStyled>
            </ActionPanelStyled>

            <FileUploadStyled
              allowMultipleFiles
              fileMimeTypes={[UPLOAD_FILE_TYPES.pdf, UPLOAD_FILE_TYPES.xlsx]}
              messageForRejectedFiles="Only PDF and XLSX files are allowed"
              shouldBeReset={fileUploadDelegate.isFileUploadReset}
              onChange={fileUploadDelegate.setFiles}
            />
          </CollapsiblePreviewSection>

          {showExhibitsTable && (
            <>
              <UploadedFilesSectionTitleStyled>Uploaded Files</UploadedFilesSectionTitleStyled>
              <StapleTable loading={isLoading} totalElements={exhibitFiles.length} minHeight="30rem" maxHeight="unset">
                <StapleTableHeader>
                  <StapleTableRow>
                    <StapleTableHeading width="16rem">Product Line</StapleTableHeading>
                    <StapleTableHeading width="30rem">File Name</StapleTableHeading>
                    <StapleTableHeadingStyled width="16rem">
                      {exhibitFiles.length > 1 && (
                        <Button
                          type="text"
                          color="primary"
                          onClick={downloadExhibits}
                          uppercase
                          leftIconProp={<Download width="2.4rem" height="2.4rem" />}
                        >
                          Download All
                        </Button>
                      )}
                    </StapleTableHeadingStyled>
                  </StapleTableRow>
                </StapleTableHeader>
                <StapleTableBody>
                  {exhibitFiles.map((file) => (
                    <StapleTableRow key={file.id}>
                      <StapleTableCell width="16rem">
                        {PRODUCT_LINES_CONTENT_MAP[file.productLineId]?.label}
                      </StapleTableCell>
                      <StapleTableCell width="30rem">
                        <FileNameStyled>{file.fileName}</FileNameStyled>
                      </StapleTableCell>
                      <StapleTableCellStyled width="16rem">
                        <ActionButtonsStyled>
                          <Tooltip alignment={AlignmentTypes.LEFT_CENTER} label="Download File">
                            <Button type="icon" color="primary" onClick={(): void => onDownloadExhibit(file.fileUrl)}>
                              <Download width="2.4rem" height="2.4rem" />
                            </Button>
                          </Tooltip>
                          <Tooltip alignment={AlignmentTypes.LEFT_CENTER} label="Delete File">
                            <Button
                              type="icon"
                              color="custom"
                              customColor={colors.red[600]}
                              onClick={(): void => {
                                void removeEnrollmentExhibits(file.productLineId, [file.fileUrl]);
                              }}
                            >
                              <Delete width="2.4rem" height="2.4rem" />
                            </Button>
                          </Tooltip>
                        </ActionButtonsStyled>
                      </StapleTableCellStyled>
                    </StapleTableRow>
                  ))}
                </StapleTableBody>
              </StapleTable>
            </>
          )}
        </>
      }
    />
  );
});

export default ManageExhibitsModal;
